// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface ParentRelationship {
  id: string;
  parent_profile_id: string;
  athlete_profile_id: string;
  status: string;
  access_level: string;
  requested_at: string;
  approved_at?: string;
  rejected_at?: string;
  athlete?: {
    id: string;
    name: string;
    avatar_url?: string;
    sport?: {
      name: string;
    };
  };
}

export function useParentRelationships() {
  const { user, profile } = useAuth();
  const [relationships, setRelationships] = useState<ParentRelationship[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id || !profile?.id) {
      console.log('🔍 useParentRelationships: No user or profile found');
      setLoading(false);
      return;
    }

    console.log('🔍 useParentRelationships: Fetching relationships for profile:', profile.id);
    fetchRelationships();
  }, [user?.id, profile?.id]);

  const fetchRelationships = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📡 Fetching parent_athlete_relationships for parent_profile_id:', profile?.id);
      
      // First, get the relationships
      const { data: relationshipsData, error: relationshipsError } = await supabase
        .from('parent_athlete_relationships')
        .select('*')
        .eq('parent_profile_id', profile?.id)
        .order('requested_at', { ascending: false });

      if (relationshipsError) {
        console.error('❌ Error fetching relationships:', relationshipsError);
        throw relationshipsError;
      }

      console.log('✅ Raw relationships data:', relationshipsData);

      if (!relationshipsData || relationshipsData.length === 0) {
        console.log('✅ No relationships found');
        setRelationships([]);
        return;
      }

      // Get athlete profile IDs
      const athleteProfileIds = relationshipsData.map(r => r.athlete_profile_id);

      // Fetch athlete profiles separately
      const { data: athleteProfiles, error: athleteError } = await supabase
        .from('profiles')
        .select(`
          id,
          name,
          avatar_url,
          sport_id
        `)
        .in('id', athleteProfileIds);

      if (athleteError) {
        console.error('❌ Error fetching athlete profiles:', athleteError);
        throw athleteError;
      }

      console.log('✅ Athlete profiles data:', athleteProfiles);

      // Fetch sports data for profiles that have sport_id
      const profilesWithSportIds = athleteProfiles?.filter(p => p.sport_id) || [];
      let sportsData: any[] = [];
      
      if (profilesWithSportIds.length > 0) {
        const sportIds = profilesWithSportIds.map(p => p.sport_id);
        const { data: sports, error: sportsError } = await supabase
          .from('sports')
          .select('id, name')
          .in('id', sportIds);

        if (sportsError) {
          console.error('❌ Error fetching sports:', sportsError);
        } else {
          sportsData = sports || [];
        }
      }

      // Combine the data
      const transformedData = relationshipsData.map(relationship => {
        const athleteProfile = athleteProfiles?.find(p => p.id === relationship.athlete_profile_id);
        let sportInfo = undefined;
        
        if (athleteProfile?.sport_id) {
          const sport = sportsData.find(s => s.id === athleteProfile.sport_id);
          sportInfo = sport ? { name: sport.name } : undefined;
        }
        
        return {
          ...relationship,
          athlete: athleteProfile ? {
            id: athleteProfile.id,
            name: athleteProfile.name,
            avatar_url: athleteProfile.avatar_url,
            sport: sportInfo
          } : undefined
        };
      });

      console.log('✅ Transformed relationships data:', transformedData);
      setRelationships(transformedData);
    } catch (err) {
      console.error('❌ Error fetching parent relationships:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const addRelationship = async (athleteProfileId: string) => {
    if (!profile?.id) return false;

    try {
      const { error } = await supabase
        .from('parent_athlete_relationships')
        .insert({
          parent_profile_id: profile.id,
          athlete_profile_id: athleteProfileId,
          status: 'pending'
        });

      if (error) throw error;

      fetchRelationships(); // Refresh the list
      return true;
    } catch (err) {
      console.error('Error adding relationship:', err);
      setError(err instanceof Error ? err.message : 'Failed to add relationship');
      return false;
    }
  };

  const removeRelationship = async (relationshipId: string) => {
    try {
      const { error } = await supabase
        .from('parent_athlete_relationships')
        .delete()
        .eq('id', relationshipId);

      if (error) throw error;

      fetchRelationships(); // Refresh the list
      return true;
    } catch (err) {
      console.error('Error removing relationship:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove relationship');
      return false;
    }
  };

  return {
    relationships,
    loading,
    error,
    addRelationship,
    removeRelationship,
    refetch: fetchRelationships
  };
}
